import { SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Routes, Route, Navigate, useLocation } from 'react-router';

import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import { accountSettings } from '@/components/organisms/Navbar/SettingsNavigation/constants';
import { SettingsNavigation } from '@/components/organisms/Navbar/SettingsNavigation/SettingsNavigation';
import { PageSubHeader, PageSubHeaderBreadcrumbs } from '@/components/organisms/PageSubHeader';
import { AccountSettings } from '@/components/pages/AccountSettings/AccountSettings';
import { ActivityLogs } from '@/components/pages/ActivityLogs/ActivityLogs';
import { DataPrivacy } from '@/components/pages/DataPrivacy/DataPrivacy';
import NotFound from '@/components/pages/NotFound/NotFound';
import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { useAccount } from '@/hooks/useAccount';
import Billing from '@/modules/billing/pages/Billing';
import Invoices from '@/modules/billing/pages/Invoices/Invoices';
import Usage from '@/modules/billing/pages/Usage/Usage';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { RoleType } from '@/shared/types/member';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { HumanAgents } from '../HumanAgents/HumanAgents';

const AccountSettingsRouter: React.FC = () => {
  const { t } = useTranslation();
  const { accountRole } = useAccount();
  const location = useLocation();
  const title_key = accountSettings.find((s) => location.pathname.includes(s.link))?.title_key;

  const canUpdateAccount = useSelector((state: RootState) =>
    getPermissions(state, 'accounts', actions.UPDATE)
  );

  const canWriteBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.WRITE)
  );

  const { billing_usage } = useSelector(selectFeatureFlags);

  const isOwner = accountRole === RoleType.OWNER;

  // Invoices and Usage supply their own top padding via SettingsContent's fill layout.
  const isFillPage = /\/account\/(invoices|usage)/.test(location.pathname);

  const isReadOnly =
    (location.pathname.includes('/account/information') && !canUpdateAccount) ||
    (location.pathname.includes('/account/human-agents') && !canUpdateAccount) ||
    (location.pathname.includes('/account/data-privacy') && !isOwner) ||
    (location.pathname.includes('/account/billing') && !canWriteBilling);

  return (
    <Layout>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <div className="flex h-8 w-8 items-center justify-center">
                      <SettingsIcon size={16} className="text-foreground-muted" />
                    </div>
                    <div className="mr-4 ml-2 h-4 w-px bg-border" />
                    <span className="text-sm leading-5 font-normal">
                      {title_key ? t(title_key) : t('profile.account_settings')}
                    </span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
      />
      {isReadOnly && <ReadOnlyBanner />}
      <div className="flex min-h-0 flex-1">
        <SettingsNavigation />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <PageContentWrapper
            noGutters={isFillPage}
            className={location.pathname.includes('/activity-logs') || isFillPage ? 'pt-0' : 'pt-8'}
          >
            <Routes>
              <Route path="/invoices" element={<Invoices />} />
              {billing_usage && <Route path="/usage" element={<Usage />} />}
              <Route path="/billing" element={<Billing />} />
              <Route path="/information" element={<AccountSettings />} />
              <Route path="/danger" element={<Navigate to="/profile/information" replace />} />
              <Route path="/activity-logs" element={<ActivityLogs />} />
              <Route path="/human-agents" element={<HumanAgents />} />
              <Route path="/data-privacy" element={<DataPrivacy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </PageContentWrapper>
        </div>
      </div>
    </Layout>
  );
};

export default AccountSettingsRouter;
